/**
 * 🚨 CRITICAL COMPONENT - SCENE BUILDER SYSTEM
 *
 * This component contains the PACK SYSTEM which is critical for multi-quantity stickers.
 *
 * ⚠️ BEFORE MODIFYING:
 * 1. Read /docs/SYSTEMS/PACK_SYSTEM.md
 * 2. Read /docs/ESSENTIAL/CRITICAL_SYSTEMS.md
 * 3. Test cases in PACK_SYSTEM.md (Ghostface 873114490 at MICRO)
 *
 * 🔒 CRITICAL RULES:
 * - NEVER change isSet to 1 for packs (must be 0)
 * - NEVER change packType to 'set' for packs (must be null or 'pack')
 * - NEVER use rotation for flipping (use isFlipped: true)
 * - ALWAYS generate unique packId per instance
 * - ALWAYS pass sizes data from sidebar to canvas
 *
 * 📍 CRITICAL SECTIONS:
 * - Lines 1084-1360: Pack creation logic
 * - Lines 426-620: Size change handling
 *
 * Last reviewed: 2025-01-15
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import SceneCanvas from './SceneCanvas';
import HomePageSidebar from './HomePageSidebar';
import MaterialIcon from './MaterialIcon';
import MainHeader from './MainHeader';
import { getColorByName, COLOR_NAMES } from '../data/colors';
import { getLargestAvailableSize, getSmallestAvailableSize } from '../data/constants';
import { SCENE_BUILDER, TIMING } from '../config/constants';
import databaseService from '../services/databaseService';
import parallaxService from '../services/parallaxService';
import sceneStorageService from '../services/sceneStorageService';
import { trackSceneBuilderAction } from '../services/analyticsService';
import logger from '../utils/logger.js';
import { extractBaseSizeName } from '../utils/inventoryUtils';
import './SceneBuilder.css';

// Helper function to safely parse JSON strings with control characters
const safeJsonParse = (jsonString) => {
  if (!jsonString) return {};
  try {
    // Clean control characters that cause JSON parsing errors
    const cleanedJson = jsonString.replace(/[\x00-\x1F\x7F]/g, '');
    return JSON.parse(cleanedJson);
  } catch (error) {
    logger.warn('⚠️ Failed to parse JSON string:', error);
    return {};
  }
};

// Asset mapping for dinosaur stickers
const getAssetPath = (listingId, title) => {
  const assetMap = {
    840465572: '/assets/images/dinosaurs/raptors_v2.png', // Raptor Pack
    1723976986: '/assets/images/dinosaurs/triceratops.png', // Triceratops
    1206470499: '/assets/images/cartoons/scooby_crew_micro.png', // Mystery Solving Great Dane and Gang
    // Add more mappings as needed
  };

  // Check exact match first
  if (assetMap[listingId]) {
    return assetMap[listingId];
  }

  // Fallback: try to match by title keywords
  const titleLower = title.toLowerCase();
  if (titleLower.includes('raptor') || titleLower.includes('velociraptor')) {
    return '/assets/images/dinosaurs/raptors_v2.png';
  }
  if (titleLower.includes('triceratops') || titleLower.includes('trike')) {
    return '/assets/images/dinosaurs/triceratops.png';
  }
  if (
    titleLower.includes('trex') ||
    titleLower.includes('t-rex') ||
    titleLower.includes('tyrannosaurus')
  ) {
    return '/assets/images/dinosaurs/trex.png';
  }
  if (titleLower.includes('stego') || titleLower.includes('stegosaurus')) {
    return '/assets/images/dinosaurs/dino_stego.png';
  }
  if (titleLower.includes('bronto') || titleLower.includes('apatosaurus')) {
    return '/assets/images/dinosaurs/bronto.png';
  }
  if (titleLower.includes('dilophosaurus') || titleLower.includes('dilo')) {
    return '/assets/images/dinosaurs/dilophosaurus_noFrill.png';
  }

  // Scooby-Doo designs
  if (titleLower.includes('mystery solving gang van') || titleLower.includes('scooby van')) {
    return '/assets/images/cartoons/scoobyDoo_mysteryMachine.png';
  }
  if (
    titleLower.includes('mystery solving great dane') ||
    titleLower.includes('scooby dog') ||
    titleLower.includes('scooby gang')
  ) {
    return '/assets/images/cartoons/scooby_crew.png';
  }

  // Default fallback
  return null;
};

const SceneBuilder = ({ adminMode = false, isInitialAppLoad = true, hasLoadedData = false }) => {
  //logger.log('🎨 SceneBuilder component loaded!');

  // State for selected stickers in the scene
  const [selectedStickers, setSelectedStickers] = useState([]);
  const [hoveredStickerId, setHoveredStickerId] = useState(null);

  // Global settings for new stickers
  const [globalSize, setGlobalSize] = useState('MICRO');
  const [globalColor, setGlobalColor] = useState('Black');
  const [zoom, setZoom] = useState(100); // Fixed: Use 100 for 100% zoom instead of 1

  // Sidebar state
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(SCENE_BUILDER.DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const sidebarRefreshRef = useRef(null);

  // Background selection
  const [selectedBackground] = useState('bkg-generic.jpg');

  // Admin modal state
  const [showParallaxSaveModal, setShowParallaxSaveModal] = useState(false);
  const [showParallaxLoadModal, setShowParallaxLoadModal] = useState(false);

  // Save scene as parallax scene - DATABASE APPROACH (FIXED)
  const handleSaveAsParallaxScene = useCallback(async () => {
    logger.log('🔄 handleSaveAsParallaxScene called', { stickerCount: selectedStickers.length });

    if (selectedStickers.length === 0) {
      alert('Please add some stickers to the scene before saving');
      return;
    }

    // Prompt for scene name
    const sceneName = prompt('Enter a name for this parallax scene:');
    if (!sceneName || sceneName.trim() === '') {
      logger.log('❌ Save cancelled - no scene name provided');
      return; // User cancelled or entered empty name
    }

    try {
      logger.log('💾 Saving scene with name:', sceneName.trim());

      // Convert selected stickers to database format
      const convertedStickers = selectedStickers.map((sticker) => {
        // Validate and clean the sticker data
        const validSize = [
          'ATOM',
          'PIXEL',
          'NANO',
          'MICRO',
          'SMALL',
          'MEDIUM',
          'LARGE',
          'JUMBO',
        ].includes(sticker.size)
          ? sticker.size
          : 'MICRO'; // Fallback to MICRO if invalid size

        const stickerData = {
          listingId: sticker.listingId?.toString(), // Ensure string format
          name: sticker.title || 'Untitled Sticker',
          size: validSize,
          color: sticker.color || 'Black',
          svgPath: sticker.svgPath || null, // Include SVG path for each sticker
          xOffset: sticker.x || 0,
          yOffset: 0, // Use 0 for Y to match working scenes
        };

        logger.log('🎯 DEBUGGING SceneBuilder: Converting sticker for save:', {
          original: {
            id: sticker.id,
            listingId: sticker.listingId,
            title: sticker.title,
            size: sticker.size,
            color: sticker.color,
            svgPath: sticker.svgPath,
          },
          converted: stickerData,
        });

        return stickerData;
      });

      logger.log(
        '🎯 DEBUGGING: Original selectedStickers:',
        selectedStickers.length,
        selectedStickers
      );
      logger.log('🎯 DEBUGGING: Converted stickers:', convertedStickers.length, convertedStickers);

      // Create scene data in the format expected by the database
      const sceneData = {
        name: sceneName.trim(),
        category: 'Scene Builder Created',
        component_height: '50vh',
        layer_y_positions: JSON.stringify({}), // Default layer positions
        layer_visibility: JSON.stringify({}), // Default layer visibility
        scene_stickers: JSON.stringify(convertedStickers),
        sticker_positions: JSON.stringify({}), // Default sticker positions
        background_path: null,
        background_style: JSON.stringify({
          size: 'cover',
          position: 'center',
          repeat: 'no-repeat',
          attachment: 'scroll',
        }),
        description: `Scene created from Scene Builder with ${convertedStickers.length} stickers`,
        created_at: new Date().toISOString(),
      };

      logger.log('📦 Scene data prepared for database:', sceneData);

      // Save to database using parallaxService
      const result = await parallaxService.saveScene(sceneData);

      if (result) {
        logger.log('✅ Scene saved to database successfully');
        alert(
          `Scene "${sceneName.trim()}" saved successfully! It will now appear in parallax component admin panels.`
        );
      } else {
        console.error('❌ Failed to save scene to database');
        alert('Failed to save scene to database. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error saving parallax scene:', error);
      alert('Failed to save scene. Please try again.');
    }
  }, [selectedStickers]);

  // Handle when SceneCanvas wants to save a parallax scene
  useEffect(() => {
    if (showParallaxSaveModal) {
      // Modal is now shown - save logic will be handled by modal button click
      // No automatic triggering - let user interact with the modal
    }
  }, [showParallaxSaveModal, handleSaveAsParallaxScene]);

  // Load scene from parallax - DATABASE APPROACH (FIXED)
  const handleLoadParallaxScene = useCallback(async () => {
    try {
      // Get all available scenes from database
      const scenes = await parallaxService.getAllScenes();
      const scenesArray = Array.isArray(scenes) ? scenes : scenes?.data || [];

      if (scenesArray.length === 0) {
        alert('No saved parallax scenes found in database.');
        return;
      }

      // Show available scenes
      const sceneList = scenesArray
        .map(
          (scene, index) =>
            `${index + 1}. ${scene.name} (${new Date(scene.created_at).toLocaleDateString()})`
        )
        .join('\n');

      const selection = prompt(`Select a scene to load:\n\n${sceneList}\n\nEnter the scene name:`);

      if (!selection || selection.trim() === '') {
        return; // User cancelled
      }

      // Find the scene by name
      const selectedScene = scenesArray.find((scene) => scene.name === selection.trim());
      if (!selectedScene) {
        alert('Scene not found. Please enter a valid scene name.');
        return;
      }

      // Load the full scene data from database
      const sceneData = await parallaxService.loadScene(selectedScene.name);
      if (!sceneData || !sceneData.data) {
        alert('Failed to load scene data from database.');
        return;
      }

      // Parse the scene stickers
      let loadedStickers = [];
      if (sceneData.data.scene_stickers) {
        try {
          const stickerData = JSON.parse(sceneData.data.scene_stickers);
          loadedStickers = stickerData.map((sticker) => ({
            id: Date.now() + Math.random(),
            title: sticker.title,
            x: sticker.xOffset || 0,
            y: sticker.yOffset || 0,
            size: sticker.size,
            color: sticker.color,
            category: sticker.category,
            svgPath: sticker.svgPath,
            listingId: sticker.listingId,
            zIndex: 1,
            // Add missing array properties to prevent undefined.length errors
            availableSizes: sticker.availableSizes || [],
            availableColors: sticker.availableColors || [],
            dimensions: sticker.dimensions || null,
            inventory: sticker.inventory || null,
            etsyUrl: sticker.etsyUrl || null,
          }));
        } catch (error) {
          console.error('❌ Error parsing scene stickers:', error);
          alert('Error parsing scene data. Scene may be corrupted.');
          return;
        }
      }

      setSelectedStickers(loadedStickers);
      alert(`Loaded scene "${selectedScene.name}" with ${loadedStickers.length} stickers.`);
    } catch (error) {
      console.error('❌ Error loading parallax scene:', error);
      alert('Failed to load scene. Please try again.');
    }
  }, []);

  // Handle when SceneCanvas wants to load a parallax scene
  useEffect(() => {
    if (showParallaxLoadModal) {
      // Modal is now shown - load logic will be handled by modal button click
      // No automatic triggering - let user interact with the modal
    }
  }, [showParallaxLoadModal, handleLoadParallaxScene]);

  // Scene persistence state
  const [isLoadingFromStorage, setIsLoadingFromStorage] = useState(true);
  const [isSidebarLoading, setIsSidebarLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showRestoreScenePrompt, setShowRestoreScenePrompt] = useState(false);

  // Combined loading state - UI controls should be hidden when either loading state is active
  const isLoading = isLoadingFromStorage || isSidebarLoading;

  // Handle sidebar loading state changes
  const handleSidebarLoadingChange = useCallback((isLoading) => {
    setIsSidebarLoading(isLoading);
  }, []);

  // Create debounced save function
  const debouncedSave = useMemo(() => sceneStorageService.setupDebouncedSave(1000), []);

  // Load scene from localStorage on mount
  useEffect(() => {
    const loadSavedScene = async () => {
      try {
        setIsLoadingFromStorage(true);
        const savedState = sceneStorageService.loadSceneState();

        if (savedState) {
          // Restore state
          if (savedState.selectedStickers?.length > 0) {
            setSelectedStickers(savedState.selectedStickers);
          }
          if (savedState.globalSize) {
            setGlobalSize(savedState.globalSize);
          }
          if (savedState.globalColor) {
            setGlobalColor(savedState.globalColor);
          }
          if (savedState.zoom) {
            setZoom(savedState.zoom);
          }
          if (typeof savedState.sidebarCollapsed === 'boolean') {
            setSidebarCollapsed(savedState.sidebarCollapsed);
          }
          if (savedState.sidebarWidth) {
            setSidebarWidth(savedState.sidebarWidth);
          }
        } else {
          // Check if there's a saved scene that could be restored later
          setTimeout(() => {
            if (sceneStorageService.hasSavedState()) {
              setShowRestoreScenePrompt(true);
            }
          }, 2000); // Wait 2 seconds before showing restore prompt
        }
      } catch (error) {
        console.error('❌ Error loading scene from storage:', error);
      } finally {
        setIsLoadingFromStorage(false);
      }
    };

    loadSavedScene();
  }, []);

  // Warn user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (hasUnsavedChanges && selectedStickers.length > 0) {
        event.preventDefault();
        event.returnValue =
          'You have unsaved changes in your scene. Are you sure you want to leave?';
        return event.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, selectedStickers.length]);

  // Auto-save scene state to localStorage when SCENE CONTENT changes
  useEffect(() => {
    // Skip saving during any loading state
    if (isLoading) return;

    const currentState = {
      selectedStickers,
      globalSize,
      globalColor,
      zoom,
      sidebarCollapsed,
      sidebarWidth,
    };

    // Use debounced save to avoid excessive localStorage writes
    debouncedSave(currentState);
    setHasUnsavedChanges(true);

    // Reset unsaved changes flag after successful save
    const timer = setTimeout(() => setHasUnsavedChanges(false), TIMING.UNSAVED_CHANGES_RESET);
    return () => clearTimeout(timer);
  }, [selectedStickers, globalSize, globalColor, zoom, isLoading, debouncedSave]);

  // Separate effect for sidebar state (UI preferences only)
  useEffect(() => {
    // Skip saving during any loading state
    if (isLoading) return;

    // Save sidebar preferences separately without triggering "unsaved changes"
    const sidebarState = {
      selectedStickers,
      globalSize,
      globalColor,
      zoom,
      sidebarCollapsed,
      sidebarWidth,
    };

    // Use debounced save for sidebar changes too, but don't mark as "unsaved changes"
    debouncedSave(sidebarState);
  }, [
    sidebarCollapsed,
    sidebarWidth,
    isLoading,
    debouncedSave,
    selectedStickers,
    globalSize,
    globalColor,
    zoom,
  ]);

  // 🎯 SIMPLE, WORKING: Handle updating a sticker in the scene
  const handleUpdateSticker = useCallback((stickerId, updates) => {
    setSelectedStickers((prev) => {
      const stickerIndex = prev.findIndex((s) => s.id === stickerId);
      if (stickerIndex === -1) return prev;

      const currentSticker = prev[stickerIndex];

      // 🚨 CRITICAL FIX: Handle color changes for pack members - update ONLY stickers in the SAME pack instance
      if (updates.color && currentSticker.isPartOfPack && currentSticker.packId) {
        return prev.map((sticker) => {
          if (sticker.packId === currentSticker.packId) {
            return {
              ...sticker,
              color: updates.color,
            };
          }
          return sticker;
        });
      }

      // 🚨 CRITICAL FIX: Handle size changes - update ONLY stickers in the SAME pack instance
      if (updates.size && updates.size !== currentSticker.size) {
        // 🎯 Check if NEW size creates a pack (even if current sticker is NOT in a pack)
        const packedSizesData = currentSticker.packedSizesData || [];
        const newSizeData = packedSizesData.find(
          (s) => s.name?.toUpperCase() === updates.size.toUpperCase()
        );
        const newPackQuantity = newSizeData?.packCount || 1;

        // 🎯 FIX SET LOGIC: Update ONLY stickers in the SAME set instance
        if (currentSticker.isPartOfPack && currentSticker.packType === 'set') {
          // Update ONLY stickers in this specific set instance
          return prev.map((sticker) => {
            if (sticker.packId === currentSticker.packId) {
              return { ...sticker, size: updates.size };
            }
            return sticker;
          });
        }

        // 🎯 PACK LOGIC: Handle size changes that affect pack counts
        if (currentSticker.isPartOfPack && currentSticker.packType === 'pack') {
          logger.log('🎯 PACK SIZE CHANGE DETECTED', {
            currentPackId: currentSticker.packId,
            oldSize: currentSticker.size,
            newSize: updates.size,
            currentPackQuantity: currentSticker.packQuantity,
            newPackQuantity: newPackQuantity,
          });

          // Remove ONLY stickers from this specific pack instance
          const filteredStickers = prev.filter((sticker) => {
            const shouldRemove = sticker.packId === currentSticker.packId;
            if (shouldRemove) {
              logger.log('🗑️ Removing pack member:', { id: sticker.id, packId: sticker.packId });
            }
            return !shouldRemove;
          });

          // Get the updated base sticker - CRITICAL: Remove isFlipped for single items
          const updatedSticker = {
            ...currentSticker,
            ...updates,
            isFlipped: false, // Reset flip state when converting from pack
            rotation: 0, // Reset rotation
          };

          // 🎯 PACK → SINGLE: Convert pack to single sticker
          if (newPackQuantity === 1) {
            logger.log('🎯 PACK → SINGLE: Converting pack to single sticker', {
              listingId: currentSticker.listingId,
              oldSize: currentSticker.size,
              newSize: updates.size,
            });

            // Create single sticker without pack properties
            const singleSticker = {
              ...updatedSticker,
              isPartOfPack: false,
              packId: null,
              packType: null,
              packQuantity: null,
              packIndex: null,
              isFlipped: false,
              rotation: 0,
            };

            return [...filteredStickers, singleSticker];
          }

          let newStickers = [updatedSticker];

          // Create pack stickers if needed
          if (newPackQuantity > 1) {
            const packStickers = [];
            // 🚨 CRITICAL: Use the SAME packId to maintain the pack instance
            const packId = currentSticker.packId;
            const spacing = SCENE_BUILDER.PACK_BASE_SPACING * SCENE_BUILDER.PACK_SPACING_MULTIPLIER;

            for (let i = 0; i < newPackQuantity; i++) {
              let offsetX = 0,
                offsetY = 0;

              if (newPackQuantity === 2) {
                offsetX = i * spacing; // Side by side
              } else if (newPackQuantity === 4) {
                offsetX = (i % 2) * spacing; // 2x2 grid
                offsetY = Math.floor(i / 2) * spacing;
              }

              // 🎯 CORRECT: PACK flipping logic
              const isFlipped =
                newPackQuantity === 2
                  ? i === 1
                  : newPackQuantity === 4
                    ? i === 1 || i === 2
                    : i % 2 === 1;

              const packSticker = {
                id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
                listingId: currentSticker.listingId,
                title: currentSticker.title,
                svgPath: currentSticker.svgPath,
                size: updates.size,
                color: currentSticker.color,
                x: currentSticker.x + offsetX,
                y: currentSticker.y + offsetY,
                isFlipped: isFlipped,
                isPartOfPack: true,
                packId: packId,
                packType: 'pack',
                packQuantity: newPackQuantity,
                packIndex: i + 1,
                zIndex: currentSticker.zIndex + i,
                dimensions: currentSticker.dimensions,
                availableSizes: currentSticker.availableSizes,
                availableColors: currentSticker.availableColors,
                category: currentSticker.category,
                description: currentSticker.description,
                packedSizesData: currentSticker.packedSizesData, // Preserve pack data
                setData: currentSticker.setData, // Preserve set data
              };
              packStickers.push(packSticker);
            }

            newStickers = [...newStickers, ...packStickers];
          }

          return [...filteredStickers, ...newStickers];
        }

        // 🎯 SINGLE ITEM becoming PACK: Check if new size creates a pack
        if (newPackQuantity > 1 && !currentSticker.isPartOfPack) {
          logger.log('🎯 SINGLE → PACK: Creating pack from single sticker', {
            listingId: currentSticker.listingId,
            oldSize: currentSticker.size,
            newSize: updates.size,
            newPackQuantity: newPackQuantity,
          });

          // Remove the single sticker
          const filteredStickers = prev.filter((s) => s.id !== stickerId);

          // Create pack stickers
          const packStickers = [];
          const uniqueInstanceId = Date.now() + Math.random().toString(36).substr(2, 9);
          const packId = `${currentSticker.listingId}-${currentSticker.color}-${uniqueInstanceId}`;
          const spacing = SCENE_BUILDER.PACK_BASE_SPACING * SCENE_BUILDER.PACK_SPACING_MULTIPLIER;

          for (let i = 0; i < newPackQuantity; i++) {
            let offsetX = 0,
              offsetY = 0;

            if (newPackQuantity === 2) {
              offsetX = i * spacing; // Side by side
            } else if (newPackQuantity === 4) {
              offsetX = (i % 2) * spacing; // 2x2 grid
              offsetY = Math.floor(i / 2) * spacing;
            }

            // Flipping logic
            const isFlipped =
              newPackQuantity === 2
                ? i === 1
                : newPackQuantity === 4
                  ? i === 1 || i === 2
                  : i % 2 === 1;

            const packSticker = {
              id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${i}`,
              listingId: currentSticker.listingId,
              title: currentSticker.title,
              svgPath: currentSticker.svgPath,
              size: updates.size,
              color: currentSticker.color,
              x: currentSticker.x + offsetX,
              y: currentSticker.y + offsetY,
              isFlipped: isFlipped,
              isPartOfPack: true,
              packId: packId,
              packType: 'pack',
              packQuantity: newPackQuantity,
              packIndex: i + 1,
              zIndex: currentSticker.zIndex + i,
              dimensions: currentSticker.dimensions,
              availableSizes: currentSticker.availableSizes,
              availableColors: currentSticker.availableColors,
              category: currentSticker.category,
              description: currentSticker.description,
              packedSizesData: currentSticker.packedSizesData,
              setData: currentSticker.setData,
              etsyUrl: currentSticker.etsyUrl,
              image: currentSticker.image,
              width: currentSticker.width,
              height: currentSticker.height,
              listing_data: currentSticker.listing_data,
              inventory_data: currentSticker.inventory_data,
              images: currentSticker.images,
              variationImages: currentSticker.variationImages,
              sizeVariations: currentSticker.sizeVariations,
              colorVariations: currentSticker.colorVariations,
            };
            packStickers.push(packSticker);
          }

          return [...filteredStickers, ...packStickers];
        }

        // 🎯 SINGLE ITEM: Just update the one sticker
        const newStickers = [...prev];
        const updatedSticker = { ...currentSticker, ...updates };
        newStickers[stickerIndex] = updatedSticker;
        return newStickers;
      }

      // Regular updates (non-pack, non-size)
      const newStickers = [...prev];
      const updatedSticker = { ...currentSticker, ...updates };
      newStickers[stickerIndex] = updatedSticker;
      return newStickers;
    });
  }, []);

  // Handle adding a sticker to the scene
  const handleAddSticker = async (stickerData) => {
    logger.log('🎯 SCENEBUILDER RECEIVED', {
      listingId: stickerData.listingId,
      title: stickerData.title,
      hasSizes: !!stickerData.sizes,
      sizes: stickerData.sizes,
    });

    // Track the sticker addition for analytics
    trackSceneBuilderAction(
      'add_sticker',
      stickerData.listingId,
      stickerData.category,
      stickerData.title
    );

    // Handle pack batches differently - add all at once
    if (stickerData.isSetBatch && stickerData.packStickers) {
      // 🚨 CRITICAL FIX: Generate unique packId for each set instance
      // This ensures each set added to the scene is completely isolated
      const uniqueInstanceId = Date.now() + Math.random().toString(36).substr(2, 9);
      const packId = `${stickerData.packId || stickerData.title}-${uniqueInstanceId}`;
      const packCount = stickerData.packStickers.length;

      // 🚨 CRITICAL: Calculate proper spacing based on sticker dimensions
      const baseSpacing = 120; // Base spacing in pixels
      const spacing = baseSpacing * 1.2; // Add some extra space for visual separation

      // 🚨 CRITICAL: Calculate pack layout dimensions
      let packCols, packRows;
      if (packCount === 2) {
        packCols = 2;
        packRows = 1; // Side by side
      } else if (packCount === 4) {
        packCols = 2;
        packRows = 2; // 2x2 grid
      } else {
        packCols = Math.ceil(Math.sqrt(packCount));
        packRows = Math.ceil(packCount / packCols);
      }

      // 🚨 CRITICAL: Calculate total pack dimensions for positioning
      const packWidth = (packCols - 1) * spacing + 100; // 100px is base sticker width
      const packHeight = (packRows - 1) * spacing + 100; // 100px is base sticker height

      // Calculate offset position for the pack
      const baseX = SCENE_BUILDER.BASE_X_POSITION;
      const baseY = SCENE_BUILDER.BASE_Y_POSITION;
      const offsetX = SCENE_BUILDER.POSITION_OFFSET_X;
      const offsetY = SCENE_BUILDER.POSITION_OFFSET_Y;

      const currentStickerCount = selectedStickers.length;
      const offsetMultiplier = currentStickerCount % SCENE_BUILDER.POSITION_CYCLE_COUNT;

      const packX = baseX + offsetX * offsetMultiplier;
      const packY = baseY + offsetY * offsetMultiplier;

      // Ensure pack fits on screen
      const maxX = SCENE_BUILDER.MAX_CANVAS_X - packWidth;
      const maxY = SCENE_BUILDER.MAX_CANVAS_Y - packHeight;
      const adjustedPackX = Math.min(packX, Math.max(0, maxX));
      const adjustedPackY = Math.min(packY, Math.max(0, maxY));

      // Create pack stickers with proper positioning
      const packStickers = [];

      for (let index = 0; index < stickerData.packStickers.length; index++) {
        const packSticker = stickerData.packStickers[index];

        // 🚨 CRITICAL: Calculate position within the pack using proper spacing
        let offsetX = 0,
          offsetY = 0;

        if (packCount === 2) {
          // 2-pack: side by side
          offsetX = index * spacing;
        } else if (packCount === 4) {
          // 4-pack: 2x2 grid
          offsetX = (index % 2) * spacing;
          offsetY = Math.floor(index / 2) * spacing;
        } else if (packCount > 4) {
          // Large packs: dynamic grid
          offsetX = (index % packCols) * spacing;
          offsetY = Math.floor(index / packCols) * spacing;
        }

        // 🚨 CRITICAL: Apply flipping logic based on pack type
        const isFlipped = stickerData.isSet
          ? false // SETS (different designs) never flipped
          : packCount === 2
            ? index === 1 // 2-pack: second one flipped
            : packCount === 4
              ? index === 1 || index === 2 // 4-pack: middle two flipped
              : index % 2 === 1; // Larger packs: alternate flipping

        // 🎯 CRITICAL: Get the COMPLETE Etsy data with pricing for this specific pack sticker
        // FIXED: Use databaseService instead of direct API call to ensure price validation
        const packUnifiedData = await databaseService.getStickerByListingId(packSticker.listingId);

        let packListingData = {};
        let packInventoryData = {};

        if (packUnifiedData && packUnifiedData.inventory_matrix) {
          try {
            const inventoryMatrix =
              typeof packUnifiedData.inventory_matrix === 'string'
                ? JSON.parse(packUnifiedData.inventory_matrix)
                : packUnifiedData.inventory_matrix;
            packInventoryData = inventoryMatrix;
            packListingData = {
              title: packUnifiedData.title,
              description: packUnifiedData.description,
              listing_id: packUnifiedData.etsyListingId,
            };
          } catch (error) {
            logger.warn(`⚠️ Failed to parse pack sticker data for ${index + 1}:`, error);
          }
        }

        // Get unified data for this specific pack sticker to get correct dimensions
        const unifiedData = await databaseService.getEtsyListingData(packSticker.listingId, true);

        // Determine the correct design-specific dimensions
        let designDimensions = {};
        let availableSizes = [];
        const availableColors = [];

        if (unifiedData?.dimensions) {
          try {
            const storedDimensions =
              typeof unifiedData.dimensions === 'string'
                ? safeJsonParse(unifiedData.dimensions)
                : unifiedData.dimensions;

            // For pack stickers, use design-specific dimensions
            if (index === 0) {
              // Main design (Design 1) - use ALL regular size names from storedDimensions
              const baseSizes = [
                'ATOM',
                'PIXEL',
                'NANO',
                'MICRO',
                'SMALL',
                'MEDIUM',
                'LARGE',
                'XLARGE',
                'JUMBO',
                'MAX',
              ];
              designDimensions = {};
              baseSizes.forEach((size) => {
                if (storedDimensions[size]) {
                  designDimensions[size] = storedDimensions[size];
                }
              });
            } else {
              // Additional designs (Design 2, 3, etc.) - use _design2 size names
              const designSuffix = index === 1 ? '_design2' : `_design${index + 1}`;
              const baseSizes = [
                'ATOM',
                'PIXEL',
                'NANO',
                'MICRO',
                'SMALL',
                'MEDIUM',
                'LARGE',
                'XLARGE',
                'JUMBO',
                'MAX',
              ];
              designDimensions = {};
              baseSizes.forEach((size) => {
                const key = `${size}${designSuffix}`;
                if (storedDimensions[key]) {
                  designDimensions[size] = storedDimensions[key];
                }
              });
            }

            availableSizes = Object.keys(designDimensions).filter((key) => designDimensions[key]);
          } catch (error) {
            logger.warn(`⚠️ Failed to parse dimensions for pack sticker ${index + 1}:`, error);
          }
        }

        packStickers.push({
          id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          listingId: packSticker.listingId || stickerData.listingId,
          title: packSticker.title,
          etsyUrl: packSticker.etsyUrl || stickerData.etsyUrl || unifiedData?.etsyUrl,
          image: packSticker.image || stickerData.image,
          svgPath: packSticker.svgPath,
          size: packSticker.size || globalSize,
          color: packSticker.color || globalColor,
          x: adjustedPackX + offsetX,
          y: adjustedPackY + offsetY,
          // NO default width/height - dimensions are calculated from sticker.dimensions in SceneCanvas
          rotation: 0,
          isPartOfPack: true,
          packId: packId,
          packType: stickerData.isSet ? 'set' : 'pack',
          setQuantity: packCount,
          designIndex: index + 1,
          designTitle: packSticker.title.split(' - ')[1] || `Design ${index + 1}`,
          isFlipped: isFlipped, // 🚨 CRITICAL: Apply proper flipping logic
          zIndex: index + 1,
          dimensions: designDimensions,
          // 🎯 CRITICAL: Use the ACTUAL Etsy data fields that exist
          listing_data: packListingData, // 🎯 CRITICAL: Use parsed Etsy listing data
          inventory_data: packInventoryData, // 🎯 CRITICAL: Use parsed Etsy inventory data
          images: unifiedData?.images || packSticker.images || [],
          variationImages: unifiedData?.variation_images || packSticker.variationImages || {},
          availableSizes: availableSizes,
          availableColors: (() => {
            const colorsInOrder = [];
            if (packInventoryData?.products) {
              packInventoryData.products.forEach((product) => {
                (product.property_values || []).forEach((prop) => {
                  if (prop.property_name === 'Primary color') {
                    (prop.values || []).forEach((v) => {
                      if (!colorsInOrder.includes(v)) colorsInOrder.push(v);
                    });
                  }
                });
              });
            }
            if (colorsInOrder.length > 0) return colorsInOrder;
            return unifiedData?.available_colors || packSticker.availableColors || [];
          })(),
          category: unifiedData?.category || packSticker.category || '',
          description: unifiedData?.description || packSticker.description || '',
          // 🎯 CRITICAL: Store pack data for synchronous access during size changes
          packedSizesData: unifiedData?.sizes || [],
          setData: stickerData.isSet
            ? {
                svgPaths: unifiedData?.svgPaths || [],
                svgTitles: unifiedData?.svgTitles || [],
              }
            : null,
          // 🎯 CRITICAL: Add variation data for direct Etsy URL generation
          sizeVariations: (() => {
            const variations = packUnifiedData?.sizeVariations || stickerData.sizeVariations || [];
            return typeof variations === 'string' ? safeJsonParse(variations) : variations;
          })(),
          colorVariations: (() => {
            const variations =
              packUnifiedData?.colorVariations || stickerData.colorVariations || [];
            return typeof variations === 'string' ? safeJsonParse(variations) : variations;
          })(),
        });
      }

      // Add all pack stickers to the scene
      setSelectedStickers((prev) => [...prev, ...packStickers]);
      return;
    }

    // Handle single sticker (existing logic)
    // Calculate offset position so stickers don't stack directly on top of each other
    // Start Y position lower to avoid placing stickers behind controls
    const baseX = SCENE_BUILDER.BASE_X_POSITION;
    const baseY = SCENE_BUILDER.BASE_Y_POSITION;
    const offsetX = SCENE_BUILDER.POSITION_OFFSET_X;
    const offsetY = SCENE_BUILDER.POSITION_OFFSET_Y;

    // Get current sticker count for offset calculation
    const currentStickerCount = selectedStickers.length;
    const offsetMultiplier = currentStickerCount % SCENE_BUILDER.POSITION_CYCLE_COUNT;

    const stickerX = baseX + offsetX * offsetMultiplier;
    const stickerY = baseY + offsetY * offsetMultiplier;

    try {
      // 🎯 CRITICAL: Get the COMPLETE Etsy data with pricing using databaseService
      // FIXED: Use databaseService instead of direct API call to ensure price validation
      const stickerUnifiedData = await databaseService.getStickerByListingId(stickerData.listingId);
      if (!stickerUnifiedData) {
        throw new Error('No unified sticker data found');
      }

      // 🎯 CRITICAL: Parse the JSON fields from the unified data
      const listingData = {
        title: stickerUnifiedData.title,
        description: stickerUnifiedData.description,
        listing_id: stickerUnifiedData.etsyListingId,
      };
      const inventoryData = stickerUnifiedData.inventory_matrix
        ? typeof stickerUnifiedData.inventory_matrix === 'string'
          ? safeJsonParse(stickerUnifiedData.inventory_matrix)
          : stickerUnifiedData.inventory_matrix
        : {};
      const imagesData = {}; // Images handled separately
      const variationImagesData = stickerUnifiedData.variation_images_data
        ? typeof stickerUnifiedData.variation_images_data === 'string'
          ? safeJsonParse(stickerUnifiedData.variation_images_data)
          : stickerUnifiedData.variation_images_data
        : {};
      const extractedVariations = {}; // Will be computed from inventory data

      // 🎯 CRITICAL: Also get unified data for sticker-specific fields
      const unifiedData = await databaseService.getEtsyListingData(stickerData.listingId, true);

      // 🔍 DEBUG: Log unified data immediately after fetch to see what sizes field contains
      logger.log('🔍 UNIFIED DATA FETCH', {
        listingId: stickerData.listingId,
        title: stickerData.title,
        hasSizes: !!unifiedData?.sizes,
        sizesRaw: unifiedData?.sizes,
        sizesType: typeof unifiedData?.sizes,
        unifiedDataKeys: unifiedData ? Object.keys(unifiedData) : 'null',
      });

      // 🚨 CRITICAL: Validate that this sticker has explicit dimensions
      // According to CORE_DEVELOPMENT_PRINCIPLES.md, stickers without explicit dimensions
      // should NOT be available for the scene builder
      let hasValidDimensions = false;

      // Check for dimensions in the unified data
      if (unifiedData?.dimensions) {
        try {
          const storedDimensions =
            typeof unifiedData.dimensions === 'string'
              ? safeJsonParse(unifiedData.dimensions)
              : unifiedData.dimensions;
          if (storedDimensions && Object.keys(storedDimensions).length > 0) {
            hasValidDimensions = true;
          }
        } catch (error) {
          logger.warn('⚠️ Failed to parse dimensions for validation:', error);
        }
      }

      // Check for dimensions in sizes field
      if (!hasValidDimensions && unifiedData?.sizes) {
        try {
          const sizesData =
            typeof unifiedData.sizes === 'string'
              ? safeJsonParse(unifiedData.sizes)
              : unifiedData.sizes;
          if (Array.isArray(sizesData) && sizesData.some((s) => s.width && s.height)) {
            hasValidDimensions = true;
          }
        } catch (error) {
          logger.warn('⚠️ Failed to parse sizes for validation:', error);
        }
      }

      // If no valid dimensions found, reject this sticker
      if (!hasValidDimensions) {
        logger.warn(
          `⚠️ Rejecting sticker ${stickerData.listingId} (${stickerData.title}) - no valid dimensions found`
        );
        throw new Error('Sticker has no valid dimensions - cannot be added to scene builder');
      }

      // Get the correct asset path for this sticker
      const assetPath = getAssetPath(stickerData.listingId, stickerData.title);

      // Determine the best size to use for this sticker
      let bestSize = globalSize;
      let fullSizeString = bestSize;

      // 🎯 CRITICAL: Get the actual Etsy color from inventory_matrix products
      // DO NOT use colorVariations - it has corrupt placeholder data like "1", "2", "3"
      // Extract real color from first product in inventory_matrix
      let actualEtsyColor = 'Black'; // Default fallback

      if (inventoryData?.products && inventoryData.products.length > 0) {
        // Get color from first product's property_values
        const firstProduct = inventoryData.products[0];
        if (firstProduct.property_values) {
          for (const prop of firstProduct.property_values) {
            if (prop.property_name === 'Primary color' && prop.values && prop.values[0]) {
              actualEtsyColor = prop.values[0];
              break;
            }
          }
        }
      }
      let sizesInOrder = [];
      if (inventoryData?.products) {
        inventoryData.products.forEach((product) => {
          (product.property_values || []).forEach((prop) => {
            if (prop.property_name === 'Size' || prop.property_name === 'Style') {
              (prop.values || []).forEach((v) => {
                if (!sizesInOrder.includes(v)) sizesInOrder.push(v);
              });
            }
          });
        });
      }

      // Fallback to sizeVariations if inventoryData is empty (extract base names only)
      if (sizesInOrder.length === 0) {
        const sizeVars =
          typeof stickerUnifiedData?.sizeVariations === 'string'
            ? safeJsonParse(stickerUnifiedData.sizeVariations)
            : stickerUnifiedData?.sizeVariations || [];
        if (Array.isArray(sizeVars)) {
          sizesInOrder = sizeVars
            .map((v) => {
              if (v?.value) {
                // Extract base size name (e.g., "NANO ($4.99)" -> "NANO")
                return v.value.split(/[\s($]/)[0].toUpperCase();
              }
              return null;
            })
            .filter(Boolean);
        }
      }

      if (sizesInOrder.length > 0) {
        // 🎯 CRITICAL: ALWAYS prioritize MICRO for scene builder (ignore globalSize)
        const microSize = sizesInOrder.find((s) => s.toUpperCase().startsWith('MICRO'));

        if (microSize) {
          fullSizeString = microSize;
          bestSize = 'MICRO';
          logger.log('✅ USING MICRO SIZE (SCENE BUILDER PRIORITY)', { fullSizeString, bestSize });
        } else {
          // 🚨 CRITICAL FIX: If MICRO doesn't exist, use the SMALLEST available
          // size (not the largest or first)
          const smallestSize = getSmallestAvailableSize(sizesInOrder);
          fullSizeString = smallestSize || sizesInOrder[0];
          bestSize = fullSizeString.split(/[\s($]/)[0].toUpperCase();
          logger.log('⚠️ USING SMALLEST AVAILABLE SIZE (NO MICRO)', {
            fullSizeString,
            bestSize,
            availableSizes: sizesInOrder,
          });
        }
      }

      // Extract simple base size name for consistent lookups (AFTER size selection)
      // If bestSize is "MICRO 4.4w x 2.1h inches", extract "MICRO"
      // If bestSize is already "MICRO" or "ATOM", it stays as is
      const baseSizeName = extractBaseSizeName(bestSize) || bestSize || 'MICRO';

      // 🚨 REMOVED: Validation logic that was overriding MICRO to LARGE
      // The dimensions field validation was causing Razorcrest to default to LARGE
      // instead of MICRO. We now trust the Etsy inventory matrix order.

      // 🎯 CRITICAL: Check ALL sizes for pack data and populate available sizes
      let packCount = 1;
      let packId = null;
      let availableSizes = [];

      // 🚨 CRITICAL FIX: Extract available sizes from ETSY INVENTORY DATA (not sizes field)
      // This ensures dropdown shows actual Etsy product variations

      // For TrueScale items, use dimensions field for available sizes
      if (stickerUnifiedData?.isTrueScale) {
        logger.log('🎯 TrueScale detected - extracting sizes from dimensions');
        if (unifiedData?.dimensions) {
          try {
            const storedDimensions =
              typeof unifiedData.dimensions === 'string'
                ? safeJsonParse(unifiedData.dimensions)
                : unifiedData.dimensions;

            // Valid size names (used to filter out corrupted entries like "_____ TRUESCALE SIZES _______")
            const validSizeNames = [
              'ATOM',
              'PIXEL',
              'NANO',
              'MICRO',
              'SMALL',
              'MEDIUM',
              'LARGE',
              'XLARGE',
              'JUMBO',
              'MAX',
            ];

            // Extract only base size names (no _design2 variants, no corrupted entries)
            availableSizes = Object.keys(storedDimensions)
              .filter((size) => !size.includes('_design'))
              .filter((size) => {
                // Filter out ANY entry that contains underscores, newlines, or special characters
                if (size.includes('_') || size.includes('\n') || size.includes('---')) {
                  logger.log('🚫 Filtering out corrupted size entry:', size);
                  return false;
                }

                // Also validate against known size names
                const upperSize = size.toUpperCase();
                const firstWord = upperSize.split(/[\s($]/)[0];
                return validSizeNames.includes(firstWord);
              });

            logger.log('✅ TrueScale available sizes from dimensions:', availableSizes);
          } catch (error) {
            logger.warn('⚠️ Failed to parse dimensions for TrueScale sizes:', error);
          }
        }
      } else {
        // 🎯 CRITICAL: For NON-TrueScale items, extract sizes from Etsy inventory matrix
        // This uses the ACTUAL Etsy product variations (sizesInOrder already computed above)
        if (sizesInOrder && sizesInOrder.length > 0) {
          availableSizes = sizesInOrder
            .map((sizeString) => {
              // Extract base size name (e.g., "MICRO" from "Micro 2.3w x 0.8h inches")
              const baseSizeName = sizeString.toUpperCase().split(/[\s($]/)[0];
              return baseSizeName;
            })
            .filter(Boolean);

          // Remove duplicates
          availableSizes = [...new Set(availableSizes)];

          logger.log('✅ Extracted sizes from Etsy inventory matrix:', {
            listingId: stickerData.listingId,
            availableSizes,
            rawSizesInOrder: sizesInOrder,
          });
        }
      }

      // Check sizes field ONLY for packCount (not for available sizes)
      if (unifiedData?.sizes) {
        try {
          const sizesData =
            typeof unifiedData.sizes === 'string'
              ? safeJsonParse(unifiedData.sizes)
              : unifiedData.sizes;

          logger.log('🔍 SIZES PARSE (packCount only)', {
            listingId: stickerData.listingId,
            sizesDataType: typeof sizesData,
            sizesDataArray: Array.isArray(sizesData),
            bestSize: bestSize,
          });

          if (Array.isArray(sizesData)) {
            // 🎯 Get pack count for current size
            const sizeObj = sizesData.find((s) => s.name?.toUpperCase() === bestSize.toUpperCase());

            logger.log('🔍 SIZE OBJ LOOKUP', {
              listingId: stickerData.listingId,
              bestSize: bestSize,
              sizeObj: sizeObj,
              hasPackCount: !!(sizeObj && sizeObj.packCount),
              packCountValue: sizeObj?.packCount,
            });

            if (sizeObj && sizeObj.packCount && sizeObj.packCount > 1) {
              packCount = sizeObj.packCount;
              logger.log('✅ PACK COUNT SET', {
                listingId: stickerData.listingId,
                packCount: packCount,
              });
              // 🚨 CRITICAL FIX: Generate unique packId for each quantity pack instance
              // This ensures each quantity pack is isolated from others
              const uniqueInstanceId = Date.now() + Math.random().toString(36).substr(2, 9);
              packId = `${stickerData.listingId}-${globalColor}-${uniqueInstanceId}`;
            }

            // DEBUG: Log pack resolution details to verify data flow
            try {
              logger.log('🐾 PACK DEBUG', {
                listingId: stickerData.listingId,
                title: stickerData.title,
                bestSize,
                sizeObjForBest: sizeObj || null,
                resolvedPackCount: packCount,
                availableSizes,
              });
            } catch (_) {}
          }
        } catch (error) {
          logger.warn('⚠️ Failed to parse sizes for packCount:', error);
        }
      }

      // If this is a quantity set (packCount > 1), create multiple stickers
      if (packCount > 1) {
        logger.log('🎯 CREATING QUANTITY PACK', {
          listingId: stickerData.listingId,
          packCount: packCount,
          packId: packId,
        });

        const quantityStickers = [];
        for (let i = 0; i < packCount; i++) {
          // Calculate offset for each sticker in the quantity set
          const offsetX = i * 30; // 30px horizontal offset between stickers
          const offsetY = Math.floor(i / 3) * 30; // New row every 3 stickers

          // 🎯 CRITICAL FIX: Flip alternate stickers horizontally (not rotate)
          // For 2-pack: flip the second one
          // For 4-pack: flip items at positions 1 and 2 (middle two)
          // For larger packs: flip every other one
          const isFlipped =
            packCount === 2 ? i === 1 : packCount === 4 ? i === 1 || i === 2 : i % 2 === 1;

          const quantitySticker = {
            id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            listingId: stickerData.listingId,
            title: unifiedData?.title || stickerData.title,
            etsyUrl: unifiedData?.etsyUrl || stickerData.etsyUrl,
            image: unifiedData?.first_image_url || stickerData.image,
            svgPath: assetPath || unifiedData?.svgPath || null,
            size: baseSizeName, // Use simple base size name for price lookups
            color: actualEtsyColor, // Use actual Etsy color from variations
            x: stickerX + offsetX,
            y: stickerY + offsetY,
            // NO default width/height - dimensions are calculated from sticker.dimensions in SceneCanvas
            rotation: 0, // No rotation for packs
            isFlipped: isFlipped, // 🚨 FIX: Use horizontal flip instead of rotation
            isPartOfPack: true,
            packId: packId,
            packType: 'pack', // 🚨 FIX: Use 'pack' not 'quantity'
            packQuantity: packCount,
            packIndex: i + 1,
            zIndex: i + 1,
            // Extract dimensions from both dimensions field and sizes field
            dimensions: (() => {
              let combinedDimensions = {};

              // Start with existing dimensions
              if (unifiedData?.dimensions) {
                try {
                  combinedDimensions =
                    typeof unifiedData.dimensions === 'string'
                      ? safeJsonParse(unifiedData.dimensions)
                      : unifiedData.dimensions;
                } catch (error) {
                  logger.warn('⚠️ Failed to parse dimensions:', error);
                }
              }

              // Add dimensions from sizes field
              if (unifiedData?.sizes) {
                try {
                  const sizesData =
                    typeof unifiedData.sizes === 'string'
                      ? safeJsonParse(unifiedData.sizes)
                      : unifiedData.sizes;

                  if (Array.isArray(sizesData)) {
                    sizesData.forEach((sizeObj) => {
                      if (
                        sizeObj.name &&
                        sizeObj.width &&
                        sizeObj.height &&
                        sizeObj.enabled !== false
                      ) {
                        combinedDimensions[sizeObj.name.toUpperCase()] = {
                          width: parseFloat(sizeObj.width),
                          height: parseFloat(sizeObj.height),
                          unit: 'inches',
                          source: 'sizes_field',
                        };
                      }
                    });
                  }
                } catch (error) {
                  logger.warn('⚠️ Failed to parse sizes for dimensions:', error);
                }
              }

              return combinedDimensions;
            })(),
            inventory: inventoryData, // 🎯 CRITICAL: Use parsed Etsy inventory data
            listing_data: listingData, // 🎯 CRITICAL: Add listing_data for pricing
            images: (() => {
              // all_image_urls is already parsed by the API - it's an array of URL strings
              const imageUrls = Array.isArray(unifiedData?.all_image_urls)
                ? unifiedData.all_image_urls
                : [];
              return imageUrls.map((url) => ({ url_fullxfull: url }));
            })(),
            variationImages: {},
            availableSizes: (() => {
              const sizes = new Set();

              // Helper function to normalize size names
              const normalizeSizeName = (sizeName) => {
                // Extract just the size name from descriptive strings like "MICRO height 0.6 inches"
                const sizeMatch = sizeName.match(/^(\w+)/);
                const baseName = sizeMatch ? sizeMatch[1] : sizeName;

                const normalized = baseName.toUpperCase().trim();

                // Filter out corrupted size variations
                if (
                  normalized.includes('_DESIGN') ||
                  normalized.includes('_DESIGN2') ||
                  normalized.includes('_design') ||
                  normalized.includes('_design2')
                ) {
                  return null; // Return null to indicate this size should be filtered out
                }

                // Map common variations to standard names
                const sizeMap = {
                  MED: 'MEDIUM',
                  LAR: 'LARGE',
                  JUM: 'JUMBO',
                  MAX: 'JUMBO',
                };
                return sizeMap[normalized] || normalized;
              };

              // 🚨 CRITICAL FIX: For TrueScale items, ONLY use dimensions field
              // Don't mix with Etsy available_sizes which may have outdated sizes
              if (unifiedData?.isTrueScale) {
                logger.log('🎯 TrueScale detected - using ONLY dimensions field');
                if (unifiedData?.dimensions) {
                  try {
                    const storedDimensions =
                      typeof unifiedData.dimensions === 'string'
                        ? safeJsonParse(unifiedData.dimensions)
                        : unifiedData.dimensions;

                    // For TrueScale, add ONLY sizes from dimensions
                    Object.keys(storedDimensions).forEach((size) => {
                      // Filter out _design2 variants from available sizes list
                      if (!size.includes('_design')) {
                        sizes.add(size);
                      }
                    });
                  } catch (error) {
                    logger.warn('⚠️ Failed to parse stored dimensions:', error);
                  }
                }
              } else {
                // For non-TrueScale items, use the existing logic
                // Use ALL stored dimensions for availableSizes - including _design2 variations
                if (unifiedData?.dimensions) {
                  try {
                    const storedDimensions =
                      typeof unifiedData.dimensions === 'string'
                        ? safeJsonParse(unifiedData.dimensions)
                        : unifiedData.dimensions;

                    // Add ALL sizes from stored dimensions (including _design2 variations)
                    Object.keys(storedDimensions).forEach((size) => {
                      sizes.add(size);
                    });
                  } catch (error) {
                    logger.warn('⚠️ Failed to parse stored dimensions for availableSizes:', error);
                  }
                }

                // Also add from sizes field to get complete size information
                if (unifiedData?.sizes) {
                  try {
                    const sizesData =
                      typeof unifiedData.sizes === 'string'
                        ? safeJsonParse(unifiedData.sizes)
                        : unifiedData.sizes;

                    if (Array.isArray(sizesData)) {
                      sizesData.forEach((sizeObj) => {
                        if (sizeObj.name && sizeObj.enabled !== false) {
                          const normalizedSize = normalizeSizeName(sizeObj.name);
                          if (normalizedSize) {
                            sizes.add(normalizedSize);
                          }
                        }
                      });
                    }
                  } catch (error) {
                    logger.warn('⚠️ Failed to parse structured sizes for availableSizes:', error);
                  }
                }

                // Add from unified available_sizes field
                if (unifiedData?.available_sizes) {
                  try {
                    const availableSizesArray =
                      typeof unifiedData.available_sizes === 'string'
                        ? JSON.parse(unifiedData.available_sizes)
                        : unifiedData.available_sizes;

                    if (Array.isArray(availableSizesArray)) {
                      availableSizesArray.forEach((sizeName) => {
                        const normalizedSize = normalizeSizeName(sizeName);
                        if (normalizedSize) {
                          sizes.add(normalizedSize);
                        }
                      });
                      logger.log('🎨 Added sizes from unified available_sizes:', Array.from(sizes));
                    }
                  } catch (error) {
                    logger.warn('⚠️ Failed to parse unified available_sizes:', error);
                  }
                }
              }

              // Convert to array and sort by priority
              const sizeArray = Array.from(sizes);
              const sizePriority = [
                'ATOM',
                'PIXEL',
                'NANO',
                'MICRO',
                'SMALL',
                'MEDIUM',
                'LARGE',
                'JUMBO',
                'MAX',
              ];

              // Sort sizes by priority order
              sizeArray.sort((a, b) => {
                const aIndex = sizePriority.indexOf(a);
                const bIndex = sizePriority.indexOf(b);
                return aIndex - bIndex;
              });

              logger.log('✅ Final available sizes:', sizeArray);
              return sizeArray;
            })(),
            availableColors: (() => {
              const colorsInOrder = [];
              if (inventoryData?.products) {
                inventoryData.products.forEach((product) => {
                  (product.property_values || []).forEach((prop) => {
                    if (prop.property_name === 'Primary color') {
                      (prop.values || []).forEach((v) => {
                        if (!colorsInOrder.includes(v)) colorsInOrder.push(v);
                      });
                    }
                  });
                });
              }
              if (colorsInOrder.length > 0) return colorsInOrder;
              return unifiedData?.available_colors || [];
            })(),
            category: unifiedData?.category || stickerData.category,
            description: unifiedData?.description || '',
            // Pack-related fields for consistency
            isSet: unifiedData?.isSet || false,
            svgPaths: unifiedData?.svgPaths || [],
            svgTitles: unifiedData?.svgTitles || [],
            // 🎯 CRITICAL: Store pack data for synchronous access during size changes
            packedSizesData: unifiedData?.sizes || [],
            setData: unifiedData?.isSet
              ? {
                  svgPaths: unifiedData?.svgPaths || [],
                  svgTitles: unifiedData?.svgTitles || [],
                }
              : null,
            // 🎯 CRITICAL: Add variation data for direct Etsy URL generation
            sizeVariations: (() => {
              const variations =
                stickerUnifiedData?.sizeVariations || stickerData.sizeVariations || [];
              return typeof variations === 'string' ? safeJsonParse(variations) : variations;
            })(),
            colorVariations: (() => {
              const variations =
                stickerUnifiedData?.colorVariations || stickerData.colorVariations || [];
              return typeof variations === 'string' ? safeJsonParse(variations) : variations;
            })(),
          };

          quantityStickers.push(quantitySticker);
        }

        // Add all quantity stickers to the scene
        setSelectedStickers((prev) => [...prev, ...quantityStickers]);
        return;
      }

      // If this is a SET listing with additional design paths, create one sticker per design
      if (
        unifiedData?.isSet &&
        Array.isArray(unifiedData?.svgPaths) &&
        unifiedData.svgPaths.length > 0
      ) {
        const groupId = `set-${stickerData.listingId}-${Date.now()}`;
        const setStickers = [];
        // Design 1 uses main svgPath
        const designPaths = [assetPath || unifiedData?.svgPath || null, ...unifiedData.svgPaths];
        // Pre-parse the full dimensions map once
        const fullDims = (() => {
          try {
            const d = unifiedData?.dimensions;
            return d ? (typeof d === 'string' ? safeJsonParse(d) : d) : {};
          } catch (_) {
            return {};
          }
        })();
        const baseSizes = [
          'ATOM',
          'PIXEL',
          'NANO',
          'MICRO',
          'SMALL',
          'MEDIUM',
          'LARGE',
          'JUMBO',
          'MAX',
        ];

        designPaths.forEach((designPath, idx) => {
          const designIndex = idx + 1; // 1-based; 1 is main
          const sizeKey = designIndex === 1 ? bestSize : `${bestSize}_design${designIndex}`;
          // Build a full dimension map for this design (so size changes work)
          const designDimMap = baseSizes.reduce((acc, base) => {
            const key = designIndex === 1 ? base : `${base}_design${designIndex}`;
            const entry = fullDims?.[key];
            if (entry && entry.width && entry.height) {
              acc[base] = {
                width: parseFloat(entry.width),
                height: parseFloat(entry.height),
                unit: entry.unit || 'inches',
              };
            }
            return acc;
          }, {});
          const availableSizeNames = Object.keys(designDimMap);
          setStickers.push({
            id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            listingId: stickerData.listingId,
            title: unifiedData?.title || stickerData.title,
            etsyUrl: unifiedData?.etsyUrl || stickerData.etsyUrl,
            image: unifiedData?.first_image_url || stickerData.image,
            svgPath: designPath,
            size: baseSizeName, // Use simple base size name for price lookups
            color: actualEtsyColor, // Use actual Etsy color from variations
            x: stickerX,
            y: stickerY,
            // NO default width/height - dimensions are calculated from sticker.dimensions in SceneCanvas
            rotation: 0,
            isPartOfPack: true,
            packId: groupId,
            zIndex: 1,
            dimensions: designDimMap,
            availableSizes: availableSizeNames,
            isSet: true,
            setData: {
              svgPaths: unifiedData?.svgPaths || [],
              svgTitles: unifiedData?.svgTitles || [],
            },
            inventory: inventoryData, // 🎯 CRITICAL: Use parsed Etsy inventory data for pricing
            listing_data: listingData, // 🎯 CRITICAL: Add listing_data for pricing
            // 🎯 CRITICAL: Add variation data for direct Etsy URL generation
            sizeVariations: (() => {
              const variations =
                stickerUnifiedData?.sizeVariations || stickerData.sizeVariations || [];
              return typeof variations === 'string' ? safeJsonParse(variations) : variations;
            })(),
            colorVariations: (() => {
              const variations =
                stickerUnifiedData?.colorVariations || stickerData.colorVariations || [];
              return typeof variations === 'string' ? safeJsonParse(variations) : variations;
            })(),
          });
        });
        setSelectedStickers((prev) => [...prev, ...setStickers]);
        return;
      }

      // Create single sticker (existing logic)
      const newSticker = {
        id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        listingId: stickerData.listingId,
        title: unifiedData?.title || stickerData.title,
        etsyUrl: unifiedData?.etsyUrl || stickerData.etsyUrl,
        image: unifiedData?.first_image_url || stickerData.image,
        svgPath: assetPath || unifiedData?.svgPath || null, // Use asset path if available, fallback to database svgPath
        size: baseSizeName, // Use simple base size name for price lookups
        color: actualEtsyColor, // Use actual Etsy color from variations
        x: stickerX, // Use calculated offset position
        y: stickerY, // Use calculated offset position
        // NO default width/height - dimensions are calculated from sticker.dimensions in SceneCanvas
        rotation: 0,
        isPartOfPack: false,
        packId: null,
        zIndex: 1,
        // Extract dimensions from both dimensions field and sizes field
        dimensions: (() => {
          let combinedDimensions = {};

          // Start with existing dimensions
          if (unifiedData?.dimensions) {
            try {
              combinedDimensions =
                typeof unifiedData.dimensions === 'string'
                  ? safeJsonParse(unifiedData.dimensions)
                  : unifiedData.dimensions;
            } catch (error) {
              logger.warn('⚠️ Failed to parse dimensions:', error);
            }
          }

          // Add dimensions from sizes field
          if (unifiedData?.sizes) {
            try {
              const sizesData =
                typeof unifiedData.sizes === 'string'
                  ? safeJsonParse(unifiedData.sizes)
                  : unifiedData.sizes;

              if (Array.isArray(sizesData)) {
                sizesData.forEach((sizeObj) => {
                  if (
                    sizeObj.name &&
                    sizeObj.width &&
                    sizeObj.height &&
                    sizeObj.enabled !== false
                  ) {
                    combinedDimensions[sizeObj.name.toUpperCase()] = {
                      width: parseFloat(sizeObj.width),
                      height: parseFloat(sizeObj.height),
                      unit: 'inches',
                      source: 'sizes_field',
                    };
                  }
                });
              }
            } catch (error) {
              logger.warn('⚠️ Failed to parse sizes for dimensions:', error);
            }
          }

          return combinedDimensions;
        })(),
        inventory: inventoryData, // 🎯 CRITICAL: Use parsed Etsy inventory data
        // 🎯 CRITICAL: Add the listing_data field for pricing
        listing_data: listingData, // 🎯 CRITICAL: Use parsed Etsy listing data
        images: (() => {
          // all_image_urls is already parsed by the API - it's an array of URL strings
          const imageUrls = Array.isArray(unifiedData?.all_image_urls)
            ? unifiedData.all_image_urls
            : [];
          return imageUrls.map((url) => ({ url_fullxfull: url }));
        })(),
        variationImages: {}, // Simplified for unified data
        availableSizes: (() => {
          // 🚨 CRITICAL FIX: For TrueScale, ONLY use dimensions field
          if (stickerUnifiedData?.isTrueScale) {
            logger.log(
              '🎯 TrueScale single sticker - using ONLY dimensions field for availableSizes'
            );
            const fallback = [];
            if (unifiedData?.dimensions) {
              try {
                const stored =
                  typeof unifiedData.dimensions === 'string'
                    ? safeJsonParse(unifiedData.dimensions)
                    : unifiedData.dimensions;
                // Filter out _design2 variants
                Object.keys(stored).forEach((k) => {
                  if (!k.includes('_design')) {
                    fallback.push(k);
                  }
                });
              } catch {}
            }
            return fallback;
          }

          // For non-TrueScale, use inventory data
          const sizesInOrder = [];
          if (inventoryData?.products) {
            inventoryData.products.forEach((product) => {
              (product.property_values || []).forEach((prop) => {
                if (prop.property_name === 'Size' || prop.property_name === 'Style') {
                  (prop.values || []).forEach((v) => {
                    if (!sizesInOrder.includes(v)) sizesInOrder.push(v);
                  });
                }
              });
            });
          }
          if (sizesInOrder.length > 0) return sizesInOrder;

          const fallback = [];

          // Try dimensions keys FIRST (clean base names like "NANO", "MICRO")
          if (unifiedData?.dimensions) {
            try {
              const stored =
                typeof unifiedData.dimensions === 'string'
                  ? safeJsonParse(unifiedData.dimensions)
                  : unifiedData.dimensions;
              Object.keys(stored).forEach((k) => fallback.push(k));
            } catch {}
          }

          // Try sizes array
          if (fallback.length === 0 && unifiedData?.sizes) {
            try {
              const sizesData =
                typeof unifiedData.sizes === 'string'
                  ? safeJsonParse(unifiedData.sizes)
                  : unifiedData.sizes;
              if (Array.isArray(sizesData))
                sizesData.forEach(
                  (s) => s?.name && !fallback.includes(s.name) && fallback.push(s.name)
                );
            } catch {}
          }

          // Extract base size names from sizeVariations if needed (strip prices)
          if (fallback.length === 0 && stickerUnifiedData?.sizeVariations) {
            try {
              const sizeVars =
                typeof stickerUnifiedData.sizeVariations === 'string'
                  ? safeJsonParse(stickerUnifiedData.sizeVariations)
                  : stickerUnifiedData.sizeVariations;
              if (Array.isArray(sizeVars)) {
                sizeVars.forEach((v) => {
                  if (v?.value) {
                    // Extract base size name (e.g., "NANO ($4.99)" -> "NANO")
                    const baseName = v.value.split(/[\s($]/)[0].toUpperCase();
                    if (baseName && !fallback.includes(baseName)) fallback.push(baseName);
                  }
                });
              }
            } catch {}
          }

          return fallback;
        })(),
        availableColors: (() => {
          const colorsInOrder = [];
          if (inventoryData?.products) {
            inventoryData.products.forEach((product) => {
              (product.property_values || []).forEach((prop) => {
                if (prop.property_name === 'Primary color') {
                  (prop.values || []).forEach((v) => {
                    if (!colorsInOrder.includes(v)) colorsInOrder.push(v);
                  });
                }
              });
            });
          }
          if (colorsInOrder.length > 0) return colorsInOrder;
          return unifiedData?.available_colors || [];
        })(),
        category: unifiedData?.category || stickerData.category,
        description: unifiedData?.description || '',
        // Pack-related fields for consistency
        isSet: unifiedData?.isSet || false,
        svgPaths: unifiedData?.svgPaths || [],
        svgTitles: unifiedData?.svgTitles || [],
        // 🎯 CRITICAL: Store pack data for synchronous access during size changes
        packedSizesData: unifiedData?.sizes || stickerData.sizes || [],
        setData: unifiedData?.isSet
          ? {
              svgPaths: unifiedData?.svgPaths || [],
              svgTitles: unifiedData?.svgTitles || [],
            }
          : null,
        // 🎯 CRITICAL: Add variation data for direct Etsy URL generation
        sizeVariations: (() => {
          const variations = stickerUnifiedData?.sizeVariations || stickerData.sizeVariations || [];
          return typeof variations === 'string' ? safeJsonParse(variations) : variations;
        })(),
        colorVariations: (() => {
          const variations =
            stickerUnifiedData?.colorVariations || stickerData.colorVariations || [];
          return typeof variations === 'string' ? safeJsonParse(variations) : variations;
        })(),
      };

      setSelectedStickers((prev) => [...prev, newSticker]);
    } catch (error) {
      console.error('❌ Error getting data:', error);

      // Get the correct asset path for this sticker (even in fallback)
      const assetPath = getAssetPath(stickerData.listingId, stickerData.title);

      // Fallback: create sticker with basic data
      const newSticker = {
        id: `sticker-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        listingId: stickerData.listingId,
        title: stickerData.title,
        etsyUrl: stickerData.etsyUrl,
        image: stickerData.image,
        svgPath: assetPath || stickerData.image, // Use asset path if available, fallback to image
        size: stickerData.size || globalSize, // Use clicked size, fallback to global
        color: stickerData.color || 'Black', // Use clicked color from sidebar data
        x: stickerX, // Use calculated offset position
        y: stickerY, // Use calculated offset position
        // NO default width/height - dimensions are calculated from sticker.dimensions in SceneCanvas
        rotation: 0,
        isPartOfPack: false,
        packId: null,
        zIndex: 1,
        category: stickerData.category,
        description: '',
        availableSizes: [], // Should be populated from database
        availableColors: [], // Should be populated from database
        dimensions: null,
        // Pack-related fields for consistency
        packType: null,
        packQuantity: null,
        isSet: false,
        svgPaths: [],
        svgTitles: [],
      };

      setSelectedStickers((prev) => [...prev, newSticker]);
    }
  };

  // Handle clearing the entire scene
  const handleClearScene = () => {
    // Track scene clear for analytics
    trackSceneBuilderAction('clear_scene', 'scene_clear', 'scene_builder');
    setSelectedStickers([]);

    // Clear localStorage as well
    sceneStorageService.clearSceneState();
    logger.log('🗑️ Scene cleared and localStorage cleaned');
  };

  // Handle restoring scene from localStorage
  const handleRestoreScene = () => {
    const savedState = sceneStorageService.loadSceneState();
    if (savedState) {
      logger.log('🔄 Manually restoring scene from localStorage...');

      if (savedState.selectedStickers?.length > 0) {
        setSelectedStickers(savedState.selectedStickers);
      }
      if (savedState.globalSize) {
        setGlobalSize(savedState.globalSize);
      }
      if (savedState.globalColor) {
        setGlobalColor(savedState.globalColor);
      }
      if (savedState.zoom) {
        setZoom(savedState.zoom);
      }
      if (typeof savedState.sidebarCollapsed === 'boolean') {
        setSidebarCollapsed(savedState.sidebarCollapsed);
      }
      if (savedState.sidebarWidth) {
        setSidebarWidth(savedState.sidebarWidth);
      }

      logger.log(`✅ Manually restored ${savedState.selectedStickers?.length || 0} stickers`);
    }
    setShowRestoreScenePrompt(false);
  };

  // Handle removing a sticker from the scene
  const handleRemoveSticker = useCallback((stickerId) => {
    // Track sticker removal for analytics
    const sticker = selectedStickers.find((s) => s.id === stickerId);
    if (sticker) {
      trackSceneBuilderAction('remove_sticker', stickerId, sticker.category, sticker.title);
    }

    setSelectedStickers((prev) => {
      // Find the sticker being removed
      const stickerToRemove = prev.find((s) => s.id === stickerId);

      // If it's part of a pack, remove the entire pack
      if (stickerToRemove?.isPartOfPack && stickerToRemove.packId) {
        return prev.filter((sticker) => sticker.packId !== stickerToRemove.packId);
      }

      // Otherwise, just remove the single sticker
      return prev.filter((sticker) => sticker.id !== stickerId);
    });
  }, []);

  // Handle sticker color change with analytics tracking
  const handleStickerColorChange = useCallback(
    (stickerId, newColor) => {
      logger.log('🎨 Changing sticker color:', stickerId, newColor);

      // Track color change for analytics
      const sticker = selectedStickers.find((s) => s.id === stickerId);
      if (sticker) {
        trackSceneBuilderAction('change_sticker_color', stickerId, sticker.category, sticker.title);
      }

      // Update the sticker color
      handleUpdateSticker(stickerId, { color: newColor });
    },
    [selectedStickers, handleUpdateSticker]
  );

  // Handle sticker size change with analytics tracking
  const handleStickerSizeChange = useCallback(
    (stickerId, newSize) => {
      // Track size change for analytics
      const sticker = selectedStickers.find((s) => s.id === stickerId);
      if (sticker) {
        trackSceneBuilderAction('change_sticker_size', stickerId, sticker.category, sticker.title);
      }

      // Update the sticker size
      handleUpdateSticker(stickerId, { size: newSize });
    },
    [selectedStickers, handleUpdateSticker]
  );

  // Handle zoom change with analytics tracking
  const handleZoomChange = useCallback((newZoom) => {
    // Track zoom change for analytics
    trackSceneBuilderAction('change_zoom', `zoom_${newZoom}`, 'scene_builder');

    setZoom(newZoom);
  }, []);

  // Handle background change with analytics tracking
  const handleBackgroundChange = useCallback((newBackground) => {
    // Track background change for analytics
    trackSceneBuilderAction('change_background', newBackground, 'scene_builder');

    // Note: selectedBackground is currently read-only, but we can track the action
  }, []);

  // Handle sidebar collapse/expand with analytics tracking
  const handleSidebarToggle = useCallback(() => {
    const newCollapsed = !sidebarCollapsed;

    // Track sidebar toggle for analytics
    trackSceneBuilderAction(
      'toggle_sidebar',
      newCollapsed ? 'collapsed' : 'expanded',
      'scene_builder'
    );

    setSidebarCollapsed(newCollapsed);
  }, [sidebarCollapsed]);

  // Handle sidebar resize with analytics tracking
  const handleSidebarResize = useCallback(
    (newWidth) => {
      // Track sidebar resize for analytics (throttled to avoid spam)
      if (Math.abs(newWidth - sidebarWidth) > 10) {
        // Only track significant changes
        trackSceneBuilderAction('resize_sidebar', `width_${newWidth}`, 'scene_builder');
      }

      setSidebarWidth(newWidth);
    },
    [sidebarWidth]
  );

  // Add pack recreation functionality to handleUpdateSticker
  handleUpdateSticker.recreatePack = (newStickers) => {
    setSelectedStickers((prev) => [...prev, ...newStickers]);
  };

  // Sidebar controls
  const toggleSidebar = () => {
    setShouldAnimate(true);
    setSidebarCollapsed(!sidebarCollapsed);
    // Reset animation flag after transition
    setTimeout(() => setShouldAnimate(false), TIMING.SIDEBAR_ANIMATION_DURATION);
  };

  // Refresh sidebar data
  const refreshSidebar = () => {
    if (sidebarRefreshRef.current) {
      sidebarRefreshRef.current();
    }
  };

  // Sidebar resize handlers
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (!isResizing) return;

      const newWidth = e.clientX;
      if (
        newWidth > SCENE_BUILDER.MIN_SIDEBAR_WIDTH &&
        newWidth < SCENE_BUILDER.MAX_SIDEBAR_WIDTH
      ) {
        setSidebarWidth(newWidth);
      }
    },
    [isResizing]
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing, handleMouseMove, handleMouseUp]);

  return (
    <div className="scene-builder">
      {/* Global Header with Navigation */}
      <MainHeader currentPage="scene-builder" />

      {/* Main Content Area Below Navigation */}
      <div className="scene-builder-content">
        {/* Sidebar positioned below navigation */}
        <div
          className="sidebar"
          style={{
            width: sidebarCollapsed
              ? `${SCENE_BUILDER.COLLAPSED_SIDEBAR_WIDTH}px`
              : `${sidebarWidth}px`,
          }}
        >
          <div
            style={{
              display: sidebarCollapsed ? 'none' : 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <HomePageSidebar
              onAddToScene={handleAddSticker}
              collapsed={false}
              onRefreshRef={sidebarRefreshRef}
              onLoadingStateChange={handleSidebarLoadingChange}
            />
          </div>

          {!isLoading && (
            <button className="sidebar-collapse-btn" onClick={toggleSidebar}>
              <MaterialIcon icon={sidebarCollapsed ? 'chevron_right' : 'chevron_left'} size={20} />
            </button>
          )}

          <div
            className="resize-handle"
            onMouseDown={handleMouseDown}
            title="Drag to resize sidebar"
          />
        </div>

        {/* Main content area - SceneCanvas handles its own header */}
        <div className="main-content-area">
          <div className="scene-area-container">
            <SceneCanvas
              stickers={selectedStickers}
              onRemoveSticker={handleRemoveSticker}
              onUpdateSticker={handleUpdateSticker}
              onClearScene={handleClearScene}
              hoveredStickerId={hoveredStickerId}
              zoom={zoom}
              setZoom={setZoom}
              background={selectedBackground}
              adminMode={adminMode}
              hasUnsavedChanges={hasUnsavedChanges}
              isLoadingFromStorage={isLoading}
              showParallaxSaveModal={showParallaxSaveModal}
              setShowParallaxSaveModal={setShowParallaxSaveModal}
              showParallaxLoadModal={showParallaxLoadModal}
              setShowParallaxLoadModal={setShowParallaxLoadModal}
              onSaveParallaxScene={handleSaveAsParallaxScene}
              onLoadParallaxScene={handleLoadParallaxScene}
              isParallaxMode={true}
            />
          </div>
        </div>
      </div>

      {/* Restore Scene Prompt Modal */}
      {showRestoreScenePrompt && (
        <div className="restore-scene-overlay">
          <div className="restore-scene-modal">
            <div className="restore-scene-header">
              <MaterialIcon icon="restore" size={24} />
              <h3>Previous Scene Found</h3>
            </div>
            <div className="restore-scene-content">
              <p>
                We found a previously saved scene with{' '}
                {sceneStorageService.getSceneSummary()?.stickerCount || 0} stickers. Would you like
                to restore it?
              </p>
              <p className="restore-scene-note">
                Your scene is automatically saved as you work, so you won't lose your progress.
              </p>
            </div>
            <div className="restore-scene-actions">
              <button className="restore-scene-btn restore-btn" onClick={handleRestoreScene}>
                <MaterialIcon icon="restore" size={16} />
                Restore Scene
              </button>
              <button
                className="restore-scene-btn dismiss-btn"
                onClick={() => setShowRestoreScenePrompt(false)}
              >
                Start Fresh
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Memoize SceneBuilder to prevent unnecessary re-renders
// Only re-render when stickers, loading, or other props actually change

// SceneBuilder has no props - it's a standalone page component
SceneBuilder.propTypes = {};

export default React.memo(SceneBuilder);
